import {
    addDays,
    addMinutes,
    isAfter,
    isWithinInterval,
    startOfDay,
    differenceInCalendarDays,
    differenceInMinutes,
} from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";
import { parseUTCDate } from 'state/helpers';


export const buildHolidayDateSet = (holidays, startDate, endDate) => {
    const set = new Set();
    holidays.forEach((h) => {
        if (h.frequency === "once") {
            let d = new Date(h.startDate);
            const hEnd = new Date(h.endDate || h.startDate);
            while (!isAfter(d, hEnd)) {
                set.add(formatInTimeZone(d, 'UTC', 'yyyy-MM-dd'));
                d = addDays(d, 1);
            }
        } else if (h.frequency === "yearly") {
            let d = new Date(startDate);
            const end = new Date(endDate);
            while (!isAfter(d, end)) {
                const hs = new Date(h.startDate);
                if (d.getUTCDate() === hs.getUTCDate() && d.getUTCMonth() === hs.getUTCMonth()) {
                    set.add(formatInTimeZone(d, 'UTC', 'yyyy-MM-dd'));
                }
                d = addDays(d, 1);
            }
        }
    });
    return set;
};

export const calcFlyingOnHolidays = (
    data, 
    holidayDateSet, 
    atdDate, 
    ataDate, 
    periodStart, 
    periodEnd
) => {    
    const flyingDates = new Set();
    for (const row of data) {
        if (!row.flightNo || !row.sector || row.pax) continue;

        const atd = row[atdDate] ? new Date(row[atdDate]) : null;
        const ata = row[ataDate] ? new Date(row[ataDate]) : null;
        
        if (!atd || !ata) continue;

        const isAtdWithin = atd >= periodStart && atd < periodEnd;
        const isAtaWithin = ata >= periodStart && ata < periodEnd;

        if (!isAtdWithin && !isAtaWithin) continue;

        const atdStr = formatInTimeZone(atd, 'UTC', 'yyyy-MM-dd');
        const ataStr = formatInTimeZone(ata, 'UTC', 'yyyy-MM-dd');
        if (holidayDateSet.has(atdStr)) flyingDates.add(atdStr);
        if (holidayDateSet.has(ataStr)) flyingDates.add(ataStr);
    }
    return flyingDates.size;
};

export const calcPerdiem = (pre, cur, perdiemConfig, addNote) => {
    const {
        PER_DIEM_CALC_METHOD = "DAILY",
        MINIMUM_TAT_MINUTES = 10,
        MINIMUM_PERDIEM_MINUTES = 360,
        MAXIMUM_PERDIEM_MINUTES = 1440,
        DOM_REPORTING_MINUTES = 60,
        INT_REPORTING_MINUTES = 75,
        DEBRIEFING_MINUTES = 15,
        // UNPAID_CODES,
        domesticAirportSet,
        // airportMap,
        periodStart,
        periodEnd,        
        CREW_PAYROLL_TIME_MODE = "LCL",
    } = perdiemConfig;

    if (
        !pre.sector || 
        !cur.sector ||
        pre.tripTypeCode !== cur.tripTypeCode ||
        pre.arrivalAirport !== cur.departureAirport ||
        cur.departureAirport === cur.crewBase 
        // || (UNPAID_CODES.includes(pre.trainingDutyIndicator) &&
        //   UNPAID_CODES.includes(cur.trainingDutyIndicator))
    ) {
        return;
    }

    if (pre.legSequenceNumber && 
        cur.legSequenceNumber && 
        cur.legSequenceNumber - pre.legSequenceNumber !== 1
    ) {
        return;
    }

    let atdDate = "atdLCLDate";
    // let ataDate = "ataLCLDate";
    if (CREW_PAYROLL_TIME_MODE === "UTC") {    
        atdDate = "atdUTCDate";
        // ataDate = "ataUTCDate";
    }
    
    let preAtd = pre[atdDate] ? new Date(pre[atdDate]) : null;

    let breakStart = pre.ataUTCDate ? new Date(pre.ataUTCDate) : null;
    let breakEnd = cur.atdUTCDate ? new Date(cur.atdUTCDate) : null;

    if (PER_DIEM_CALC_METHOD === "DAILY") {
        breakStart = addMinutes(breakStart, DEBRIEFING_MINUTES);

        if (domesticAirportSet.has(cur.departureAirport)) {
            breakEnd = addMinutes(breakEnd, -DOM_REPORTING_MINUTES);
        } else {
            breakEnd = addMinutes(breakEnd, -INT_REPORTING_MINUTES);
        }
    }

    const breakMinutes = differenceInMinutes(breakEnd, breakStart);
    
    const addPerdiem = (row, tafb, perdiem, arrivalAirport) => {
        row.tafb = tafb;
        const isDomestic = domesticAirportSet.has(arrivalAirport);
        if (isDomestic) {
            row.domPerdiem = perdiem;
        } else {
            row.intPerdiem = perdiem;
        }
    };

    // Check TAT
    if (breakMinutes < MINIMUM_TAT_MINUTES) {
        cur.note = addNote(cur.note, `TAT ${breakMinutes} mins`);
    }

    // Check Perdiem eligibility
    if (breakMinutes > MINIMUM_PERDIEM_MINUTES) {
        if (breakMinutes > MAXIMUM_PERDIEM_MINUTES) {
            cur.note = addNote(cur.note, `Perdiem ${(breakMinutes / 60).toFixed(1)} hrs`);
        } else {      
            if (PER_DIEM_CALC_METHOD === "HOURLY") {
                if (!(breakStart >= periodEnd || breakEnd <= periodStart)) {
                    const perdiemStart = breakStart < periodStart ? periodStart : breakStart;
                    const perdiemEnd = breakEnd > periodEnd ? periodEnd : breakEnd;

                    if (perdiemEnd > perdiemStart) {
                        const perdiemHours = differenceInMinutes(perdiemEnd, perdiemStart) / 60;
                        
                        if (breakEnd > periodEnd) {
                            pre.perdiem = perdiemHours;
                            addPerdiem(pre, perdiemHours, perdiemHours, pre.arrivalAirport);
                        } else {
                            cur.perdiem = perdiemHours;
                            addPerdiem(cur, perdiemHours, perdiemHours, pre.arrivalAirport);
                        }
                    }
                }
            } else if (PER_DIEM_CALC_METHOD === "DAILY") {        
                if (preAtd >= periodStart && 
                    preAtd <= periodEnd
                ) {                  
                    const PER_DIEM_BLOCK_MINUTES = 720; // 12h
                    const PER_DIEM_BLOCK_DAYS = 0.5;

                    const blocks = Math.floor(breakMinutes / PER_DIEM_BLOCK_MINUTES);
                    const perdiemDays = PER_DIEM_BLOCK_DAYS + blocks * PER_DIEM_BLOCK_DAYS;
                    
                    pre.perdiem = perdiemDays;
                    addPerdiem(pre, breakMinutes / 60, perdiemDays, pre.arrivalAirport);        
                }
            }
        }
    }
}

export const generateDaysColumns = (
    processedRostersByCrew, 
    startDate, 
    endDate, 
    MINIMUM_OVERLAP_WORKDAY_MINUTES = 0,
    CREW_PAYROLL_TIME_MODE = "LCL",
) => {
    const columns = {};
    const daysDiff = differenceInCalendarDays(endDate, startDate); // 1 -> 30 = 29 daysDiff
    
    let atdDate = "atdLCLDate";
    let ataDate = "ataLCLDate";
    if (CREW_PAYROLL_TIME_MODE === "UTC") {    
        atdDate = "atdUTCDate";
        ataDate = "ataUTCDate";
    }
    
    for (let i = 0; i <= daysDiff; i++) {
        const date = formatInTimeZone(addDays(startDate, i), 'UTC', "yyyy-MM-dd"); 
        columns[date] = "";         
    }
    
    processedRostersByCrew.forEach((row) => {
        const atd = parseUTCDate(toZonedTime(row[atdDate], "UTC"));
        const ata = parseUTCDate(toZonedTime(row[ataDate], "UTC"));
        const isAtdWithinPeriod = isWithinInterval(atd, { start: startDate, end: endDate });
        const isAtaWithinPeriod = isWithinInterval(ata, { start: startDate, end: endDate });
        
        if (isAtdWithinPeriod || isAtaWithinPeriod) {
            const atdKey = formatInTimeZone(row[atdDate], "UTC", "yyyy-MM-dd"); 
            const ataKey = formatInTimeZone(row[ataDate], "UTC", "yyyy-MM-dd"); 
            const tripCode = row.tripTypeCode || row.flightNo || "";
            
            // Check if tripTypeCode already exists; if not, append
            if (columns[atdKey] !== undefined && !columns[atdKey]?.includes(tripCode)) {
                columns[atdKey] = columns[atdKey]
                    ? `${columns[atdKey]}, ${tripCode}`
                    : tripCode;
            }

            // Whether a flight
            if (row.dutyDate && row.tripTypeCode) {
                if (isAtaWithinPeriod) {
                    const daysDiff = differenceInCalendarDays(
                        toZonedTime(row[ataDate], 'UTC'),
                        toZonedTime(row[atdDate], 'UTC')
                    );

                    if (daysDiff > 0) {
                        const end = toZonedTime(row[ataDate], 'UTC');
                        const startOfDayUTC = startOfDay(end); 

                        const minutesDiff = differenceInMinutes(end, startOfDayUTC);
                        if (minutesDiff > MINIMUM_OVERLAP_WORKDAY_MINUTES) {
                            if (columns[ataKey] !== undefined && !columns[ataKey]?.includes(tripCode)) {
                                columns[ataKey] = columns[ataKey]
                                    ? `${columns[ataKey]}, ${tripCode}`
                                    : tripCode;
                            }
                        }
                    }
                }
            } else {
                if (columns[ataKey] !== undefined && !columns[ataKey]?.includes(tripCode)) {
                    columns[ataKey] = columns[ataKey]
                        ? `${columns[ataKey]}, ${tripCode}`
                        : tripCode;
                }
            }
        }
    });

    return columns;
};              